import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../../lib/axios';
import {
    Box, Typography, CircularProgress, Tooltip, IconButton, Autocomplete, TextField, Grid,
    Button, FormLabel, RadioGroup, FormControlLabel, Radio, Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import { usePermissions } from '../../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import HomeIcon from '@mui/icons-material/Home';
import { PhotoCamera } from '@mui/icons-material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../../AgGridStyle.css';
import CustomAutocomplete from '../../AutoComplete/AutoComplete';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const BroadcastingTable = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [recipientType, setRecipientType] = useState("R"); // R=Resident, P=Party, U=User
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const [selectedRowsCount, setSelectedRowsCount] = useState(0);
    const [UserId, setUserId] = useState(sessionStorage.getItem('UserId'));
    const [openDialog, setOpenDialog] = useState(false);
    const [brdCast, setBrdCast] = useState([]);
    const [autoMail, setAutoMail] = useState(true);
    const [mailType, setMailType] = useState('T');
    const [image, setImage] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({
        BrdCastId: "",
        BrdCastNo: "",
        BrdCastTitle: "",
        BrdCastSubj: "",
        BrdCastDesc: "",
        BrdCastDt: "",
        Remark: "",
        Attachments: "",
        Email: "",
    });

    // Permissions for each type
    const residentPerm = usePermissions("Resident Master");
    const partyPerm = usePermissions("Vendor Master");
    const userPerm = usePermissions("User Master");

    const permissions = recipientType === "R" ? residentPerm :
        recipientType === "P" ? partyPerm : userPerm;
    const { AddRec = "0" } = permissions || {};

    // Dynamic Columns – Exact copy from your three tables
    const colDefs = useMemo(() => {
        if (recipientType === "R") {
            return [
                {
                    field: 'RowNum', headerName: 'Sr.No', width: 80, checkboxSelection: true,
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                },
                { field: 'FirstName', headerName: 'FirstName', width: 90 },
                { field: 'MidName', headerName: 'MiddleName', width: 100 },
                { field: 'LastName', headerName: 'LastName', width: 90 },
                { field: 'Gender', headerName: 'Gender', width: 70 },
                { field: 'Mobile', headerName: 'Mobile', width: 100 },
                { field: 'Email', headerName: 'Email', width: 170 },
                { field: 'AadharNo', headerName: 'AadharNo', width: 110 },
                { field: 'panNo', headerName: 'Pan No', width: 110 },
                { field: 'PinCode', headerName: 'PinCode', width: 80 },
                { field: 'AreaName', headerName: 'Area', flex: 1 },
                {
                    field: 'Status', headerName: 'Status', width: 80,
                    cellStyle: (params) => ({
                        color: params.value === 'Active' || params.value === '1' ? 'green' : 'red',
                        fontWeight: 'bold',
                    })
                },
                { field: 'Remark', headerName: 'Remark', flex: 1 },
            ];
        }

        if (recipientType === "P") {
            return [
                {
                    field: 'RowNum',
                    headerName: 'Sr.No',
                    width: 70,
                    checkboxSelection: true,
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                },
                { field: 'PartyId', headerName: 'Code', width: 100 },
                { field: 'PartyName', headerName: 'PartyName', flex: 1 },
                { field: 'GsttinNo', headerName: 'GST No', flex: 1 },
                { field: 'PartyAbrv', headerName: 'Abrv', flex: 1 },
                { field: 'MobileNo', headerName: 'Mobile', width: 140 },
                { field: 'ContactPerson', headerName: 'ContactPerson', flex: 1 },
                {
                    field: 'Status', headerName: 'Status', width: 100,
                    cellStyle: (params) => ({
                        color: params.value === 'Active' || params.value === '1' ? 'green' : 'red',
                        fontWeight: 'bold',
                    })
                },
            ];
        }

        // User Master – exact same as your UserMasterTable
        return [
            {
                field: 'RowNum', headerName: 'Sr.No', width: 80, checkboxSelection: true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
            },
            { field: 'UserName', headerName: 'UserName', width: 200 },
            { field: 'UtType', headerName: 'User Type', width: 200 },
            { field: 'MobileNo', headerName: 'Mobile No', width: 200 },
            { field: 'EmailId', headerName: 'Email', width: 300 },
            {
                field: 'Status', headerName: 'Status', width: 130,
                cellStyle: (params) => ({
                    color: params.value === 'Active' || params.value === '1' ? 'green' : 'red',
                    fontWeight: 'bold',
                })
            },
            { field: 'Remark', headerName: 'Remark', width: 300 },
        ];
    }, [recipientType]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);

    useEffect(() => {
        const fetchBroadCast = async () => {
            try {
                const response = await axiosInstance.post('MstBrdCast/getMstBrdCastdrp', {
                    Flag: ""
                });
                if (response.data.Status === 0 && Array.isArray(response.data.Data)) {
                    const formattedData = response.data.Data.map(item => ({
                        Id: item.BrdCastId,
                        Name: item.BrdCastTitle,
                        Subject: item.BrdCastSubj,
                        Remark: item.BrdCastDesc,
                    }));
                    setBrdCast(formattedData);

                    if (formData.BrdCastId) {
                        const selectedBroadcast = formattedData.find(item => item.Id === formData.BrdCastId);
                        if (selectedBroadcast) {
                            setFormData({
                                ...formData,
                                BrdCastSubj: selectedBroadcast.Subject,
                                BrdCastDesc: selectedBroadcast.Remark,
                            });
                        }
                    } else {
                        const firstBroadcast = formattedData[0];
                        setFormData({
                            ...formData,
                            BrdCastId: firstBroadcast.Id,
                            BrdCastSubj: firstBroadcast.Subject,
                            BrdCastDesc: firstBroadcast.Remark,
                        });
                    }
                }
            } catch (error) {
                toast.error("Error while loading broadcast.");
            }
        };
        fetchBroadCast();
    }, [formData.BrdCastId]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setRowData([]);

        try {
            let endpoint = '';
            if (recipientType === "R") endpoint = "ResidentMst/getAllResidentMstDashBoard";
            else if (recipientType === "P") endpoint = "PartyMst/GetPartyMstDashBoard";
            else if (recipientType === "U") endpoint = "UserMst/getAllUserMstDashBoard";

            const response = await axiosInstance.post(endpoint, {
                start: 1,
                PageSize: 1,
                SearchText: ""
            });

            if (response.data.Status === 0) {
                const formatted = response.data.Data.map((item, idx) => ({
                    ...item,
                    RowNum: idx + 1,
                    Status: item.Status == 1 || item.Status === "1" ? 'Active' : 'Inactive',
                    Email: item.Email || item.EmailId || '',
                }));
                setRowData(formatted);
            } else {
                toast.info("No records found");
                setRowData([]);
            }
        } catch (err) {
            toast.error("Failed to load data");
            setRowData([]);
        } finally {
            setIsLoading(false);
        }
    }, [recipientType]);

    useEffect(() => {
        fetchData();
    }, [fetchData, globalProperty]);

    const handleAddNew = () => {
        if (AddRec !== "1") return;
        if (recipientType === "R") navigate('/resident');
        else if (recipientType === "P") navigate('/partymaster');
        else if (recipientType === "U") navigate('/usermst');
    };

    const handleSendEmail = async () => {
        const selectedNodes = gridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length === 0) {
            toast.warning("Please select at least one recipient.");
            return;
        }

        const recipientEmails = selectedNodes.map(node => {
            const data = node.data;
            if (recipientType === "R") return data.Email?.trim();
            if (recipientType === "P") return (data.Email || data.EmailId || '').trim();
            if (recipientType === "U") return data.EmailId?.trim();
            return null;
        }).filter(email => email && email.includes('@'));

        if (recipientEmails.length === 0) {
            toast.error("No valid email addresses found in selected rows.");
            return;
        }

        const userId = parseInt(sessionStorage.getItem('UserId')) || 0;
        const propertyId = globalProperty || 0;

        const subject = mailType === "T" ? formData.BrdCastSubj : formData.BrdCastSubj || "Message from Clanbridge";
        const body = mailType === "T" ? formData.BrdCastDesc : formData.BrdCastDesc || "";

        // Use real filename if imageFile exists
        const fileName = imageFile ? imageFile.name : null;
        const base64Data = image && image.includes('base64,') ? image : null;

        const payload = {
            ToEmail: recipientEmails,
            Subject: subject,
            Body: body,
            PropId: propertyId,
            UserId: userId,
            ImgFolderNm: formData.BrdCastNo || "Broadcast",
            DocAttachments: imageFile && base64Data ? [{
                FileData: base64Data,
                FileName: fileName,
            }] : [],
            TblName: "TrnBrdCast",
            Id: 0,
            EmailType: mailType,
            Remark: ""
        };

        try {
            setIsLoading(true);
            const response = await axiosInstance.post("EmailRequest/SendEmail", payload);

            if (response.data.Status === 0) {
                toast.success(response.data.Message || `Email sent to recipient`);
                setOpenDialog(false);
                setImage(null);
                setImageFile(null);
            } else {
                toast.error(response.data.Message || "Failed to send email");
            }
        } catch (error) {
            toast.error("Error sending email");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAutoMail = (e) => {
        setAutoMail(e.target.checked);
    };

    const handleSendSms = () => {
        toast.info("Work in progress.");
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error("Only images (JPG, PNG, GIF) and PDF are allowed.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.warn("File size should be less than 10MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64Data = reader.result;
            setImage(base64Data);
            setImageFile(file);
        };
        reader.readAsDataURL(file);
    };

    const clearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    const onSelectionChanged = useCallback(() => {
        const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
        setSelectedRowsCount(selectedRows.length);
    }, []);

    const handleInputChange = (e, value) => {
        const { name, value: newValue } = e.target;

        setFormData(prevState => {
            const updatedFormData = {
                ...prevState,
                [name]: newValue,
            };

            // If the selected field is BrdCastId (Broadcast Template), auto-fill subject and description
            if (name === 'BrdCastId' && value) {
                const selectedBroadcast = brdCast.find(item => item.Id === newValue);
                if (selectedBroadcast) {
                    updatedFormData.BrdCastSubj = selectedBroadcast.Subject;
                    updatedFormData.BrdCastDesc = selectedBroadcast.Remark;
                }
            }

            return updatedFormData;
        });
    };

    return (
        <Box sx={{ width: '100%', height: '90vh', p: '10px', bgcolor: 'rgb(249,249,249)', position: 'relative' }}>
            <ToastContainer />

            <Box sx={{
                bgcolor: 'white',
                p: { xs: "8px 16px", sm: "12px 16px" },
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                mx: "auto",
                my: "10px",
                maxWidth: "95%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Breadcrumbs separator="›" aria-label="breadcrumb">
                        <Link
                            underline="hover"
                            color="#635bff"
                            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                            onClick={() => navigate('/dashboard')}
                        >
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            <Box component="span">Home</Box>
                        </Link>
                        <Link underline="hover" color="#635bff" onClick={() => navigate('/utilities')} sx={{ cursor: 'pointer' }}>
                            Utilities & Others
                        </Link>
                        <Typography color="text.primary" sx={{ fontWeight: "500" }}>Broadcasting</Typography>
                    </Breadcrumbs>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormLabel sx={{ fontWeight: 'bold', color: '#000' }}>Send To →</FormLabel>
                        <RadioGroup row value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
                            <FormControlLabel value="R" control={<Radio size="small" />} label="Resident" />
                            <FormControlLabel value="U" control={<Radio size="small" />} label="User" />
                            <FormControlLabel value="P" control={<Radio size="small" />} label="Party/Service Provider" />
                        </RadioGroup>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title="Clear all applied filters" arrow>
                        <span>
                            <Button variant="outlined" size="small" onClick={clearFilters}
                                sx={{ borderRadius: "50px" }}>
                                Clear Filter
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Send SMS" arrow>
                        <span>
                            <Button variant="contained" size="small" onClick={handleSendSms}
                                startIcon={<SmsIcon />}
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: "6px 15px",
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                    "&:hover": {
                                        background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                                    }
                                }}>
                                Sms
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Click To Send Mail" arrow>
                        <span>
                            <Button variant="contained" size="small" onClick={() => setOpenDialog(true)}
                                startIcon={<EmailIcon />}
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: "6px 15px",
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                    "&:hover": {
                                        background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                                    }
                                }}
                            >
                                Mail
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={AddRec === "1" ? "Add New Record" : "No permission"} arrow>
                        <span>
                            <Button variant="contained" size="small" onClick={handleAddNew} disabled={AddRec !== "1"}
                                startIcon={<AddIcon />}
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: "6px 15px",
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                    "&:hover": {
                                        background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                                    }
                                }}>
                                New
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title='Previous Page' arrow>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
                            <IconButton
                                onClick={() => navigate('/utilities')}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 35,
                                    height: 35,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine" sx={{ height: 'calc(97% - 56px)', width: '95%', mx: 'auto', position: 'relative' }} >
                {isLoading ? (
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
                        <Typography sx={{ mt: 2, color: '#635bff', fontWeight: 'bold' }}>Loading data...</Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        paginationPageSizeSelector={[100, 500, 1000, 5000]}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        rowMultiSelectWithClick={true}
                        onSelectionChanged={onSelectionChanged}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        overlayNoRowsTemplate="No recipients found"
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
                        })}
                    />
                )}
            </Box>

            <Box sx={{ position: 'absolute', bottom: 16, left: 70, fontSize: '14px' }}>
                Total Records: <b>{rowData.length}</b> | Selected: <b>{selectedRowsCount}</b>
            </Box>

            <Dialog maxWidth="md" fullWidth={true} open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Select Broadcast Templates</span>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormLabel sx={{ fontWeight: 'bold', color: '#000' }}>Send Mail ✉</FormLabel>
                        <RadioGroup
                            row
                            value={mailType}
                            onChange={(e) => setMailType(e.target.value)}
                        >
                            <FormControlLabel value="T" control={<Radio size="small" />} label="Template" />
                            <FormControlLabel value="M" control={<Radio size="small" />} label="Manual" />
                        </RadioGroup>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1, px: 3 }}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4} lg={12}>
                                <CustomAutocomplete
                                    options={brdCast}
                                    id="BrdCastId-autocomplete"
                                    name="BrdCastId"
                                    label={<span>Broadcast Templates<span style={{ color: 'red' }}>*</span></span>}
                                    value={brdCast.find(itm => itm.Id === formData.BrdCastId) || ''}
                                    onChange={(event, value) => handleInputChange(event, value?.Id)}
                                    className="custom-textfield"
                                    variant='filled'
                                />
                            </Grid>
                            <Grid item xs={12} md={4} lg={12}>
                                <TextField
                                    id='BrdCastSubj'
                                    name='BrdCastSubj'
                                    label='Subject'
                                    variant='filled'
                                    fullWidth
                                    className='custom-textfield'
                                    value={formData.BrdCastSubj}
                                    onChange={handleInputChange}
                                    disabled={mailType === "T"}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} lg={9}>
                                <TextField
                                    id='BrdCastDesc'
                                    name='BrdCastDesc'
                                    label='Message'
                                    variant='filled'
                                    fullWidth
                                    multiline
                                    rows={3}
                                    className='custom-textfield'
                                    value={formData.BrdCastDesc}
                                    onChange={handleInputChange}
                                    disabled={mailType === "T"}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} lg={3}>
                                <Box
                                    sx={{
                                        border: '1px dashed #070000ff',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f9f9f9',
                                        textAlign: 'center',
                                        height: '150px',
                                    }}
                                >
                                    <input
                                        accept="image/*"
                                        id="upload-image"
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUpload}
                                    />

                                    {image ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <img
                                                src={image}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                    borderRadius: '8px',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            No image selected
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {image ? (
                        <Button
                            size='small'
                            variant="contained"
                            color="error"
                            onClick={() => setImage(null)}
                        >
                            Clear
                        </Button>
                    ) : (
                        <Button
                            size='small'
                            startIcon={<PhotoCamera />}
                            variant="contained"
                            color="success"
                            disabled={mailType === "T"}
                            onClick={() => document.getElementById('upload-image').click()}
                        >
                            Upload
                        </Button>
                    )}
                    <Button variant="contained" size='small' onClick={handleSendEmail} color="primary">Send</Button>
                    <Button variant="outlined" size='small' onClick={() => setOpenDialog(false)} color="error">Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default BroadcastingTable;